import React from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import TaxPreparation from './TaxPreparation';
import TaxPlanning from './TaxPlanning';
import IRSRepresentation from './IRSRepresentation';
import BusinessTaxConsulting from './BusinessTaxConsulting';
import QuarterlyEstimates from './QuarterlyEstimates';
import PayrollSalesTax from './PayrollSalesTax';
import TaxForSCorps from './TaxForSCorps';
import TaxForPartnerships from './TaxForPartnerships';
import TaxForFreelancers from './TaxForFreelancers';
import TaxForRealEstate from './TaxForRealEstate';
import TaxForInternational from './TaxForInternational';
import TaxForStartups from './TaxForStartups';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .ts-page * { box-sizing: border-box; margin: 0; padding: 0; }
  .ts-page { font-family: 'Inter', sans-serif; color: #0f172a; background: #ffffff; width: 100%; }

  /* ─── Hero ─────────────────────────────────────────────────── */
  .ts-hero { position: relative; padding: 100px 24px 120px; overflow: hidden; background: #fff; }
  .ts-hero-blob1 { position: absolute; top: -8%; right: -4%; width: 520px; height: 520px; border-radius: 50%; background: radial-gradient(circle, rgba(16,185,129,0.18), rgba(5,150,105,0.08)); filter: blur(80px); pointer-events: none; }
  .ts-hero-blob2 { position: absolute; bottom: -12%; left: -8%; width: 580px; height: 580px; border-radius: 50%; background: radial-gradient(circle, rgba(245,158,11,0.12), rgba(251,191,36,0.06)); filter: blur(90px); pointer-events: none; }
  .ts-hero-inner { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 72px; align-items: center; position: relative; z-index: 1; }
  @media (max-width: 900px) { .ts-hero-inner { grid-template-columns: 1fr; } .ts-hero-visual { display: none; } }
  .ts-hero-badge { display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; border-radius: 999px; background: #f0fdf4; border: 1px solid #d1fae5; font-size: 13px; font-weight: 500; color: #065f46; margin-bottom: 28px; }
  .ts-badge-dot { width: 7px; height: 7px; border-radius: 50%; background: #10b981; display: inline-block; animation: ts-pulse 2s infinite; }
  @keyframes ts-pulse { 0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(16,185,129,0.4); } 50% { opacity: 0.85; box-shadow: 0 0 0 6px rgba(16,185,129,0); } }
  .ts-hero h1 { font-size: clamp(1.8rem, 3.75vw, 3rem); font-weight: 900; line-height: 1.1; letter-spacing: -0.03em; color: #0f172a; margin-bottom: 24px; }
  .ts-gradient-text { background: linear-gradient(135deg, #059669 0%, #0ea5e9 60%, #10b981 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .ts-gold-text { background: linear-gradient(135deg, #d97706, #f59e0b); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .ts-hero p { font-size: 1.15rem; color: #475569; line-height: 1.75; margin-bottom: 40px; font-weight: 300; max-width: 520px; }
  .ts-btn-group { display: flex; gap: 14px; flex-wrap: wrap; }
  .ts-btn-primary { padding: 14px 28px; background: #0f172a; color: #fff; border: none; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s; }
  .ts-btn-primary:hover { background: #059669; box-shadow: 0 8px 24px rgba(5,150,105,0.35); transform: translateY(-1px); }
  .ts-btn-secondary { padding: 14px 28px; background: #fff; color: #334155; border: 1.5px solid #e2e8f0; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
  .ts-btn-secondary:hover { border-color: #6ee7b7; background: #f0fdf4; color: #065f46; }

  /* Hero visual card */
  .ts-hero-visual { background: #fff; border-radius: 28px; border: 1px solid #e2e8f0; padding: 32px; box-shadow: 0 24px 64px rgba(5,150,105,0.1); position: relative; overflow: hidden; }
  .ts-visual-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; padding-bottom: 20px; border-bottom: 1px solid #f1f5f9; }
  .ts-visual-title p:first-child { font-size: 12px; color: #94a3b8; font-weight: 500; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px; }
  .ts-visual-title p:last-child { font-size: 18px; font-weight: 800; color: #0f172a; }
  .ts-visual-icon { width: 48px; height: 48px; background: linear-gradient(135deg, #059669, #10b981); border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 22px; }
  .ts-saving-item { display: flex; justify-content: space-between; align-items: center; padding: 14px 16px; border-radius: 12px; background: #f8fafc; border: 1px solid #f1f5f9; margin-bottom: 10px; transition: all 0.2s; }
  .ts-saving-item:hover { background: #f0fdf4; border-color: #d1fae5; }
  .ts-saving-left { display: flex; align-items: center; gap: 10px; font-size: 14px; font-weight: 600; color: #334155; }
  .ts-saving-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .ts-saving-right { font-size: 13px; font-weight: 700; color: #059669; }
  .ts-visual-total { margin-top: 20px; padding: 18px; background: linear-gradient(135deg, #f0fdf4, #ecfdf5); border: 1px solid #d1fae5; border-radius: 14px; display: flex; justify-content: space-between; align-items: center; }
  .ts-visual-total span:first-child { font-size: 14px; font-weight: 600; color: #374151; }
  .ts-visual-total span:last-child { font-size: 20px; font-weight: 900; color: #059669; }
  .ts-visual-badge { margin-top: 16px; padding: 12px 16px; background: #0f172a; border-radius: 12px; display: flex; align-items: center; gap: 10px; }
  .ts-visual-badge-dot { width: 8px; height: 8px; min-width: 8px; border-radius: 50%; background: #4ade80; box-shadow: 0 0 8px rgba(74,222,128,0.8); display: block; }
  .ts-visual-badge span.ts-badge-text { font-size: 13px; color: #e2e8f0; font-weight: 500; line-height: 1.4; }

  /* ─── Stats Bar ─────────────────────────────────────────────── */
  .ts-stats { background: #fff; border-top: 1px solid #f1f5f9; border-bottom: 1px solid #f1f5f9; }
  .ts-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); max-width: 1200px; margin: 0 auto; }
  @media (max-width: 768px) { .ts-stats-grid { grid-template-columns: repeat(2, 1fr); } }
  .ts-stat { padding: 36px 24px; text-align: center; border-right: 1px solid #f1f5f9; position: relative; }
  .ts-stat:last-child { border-right: none; }
  @media (max-width: 768px) { .ts-stat:nth-child(2n) { border-right: none; } .ts-stat { border-bottom: 1px solid #f1f5f9; } }
  .ts-stat-num { font-size: 2.2rem; font-weight: 900; letter-spacing: -0.03em; line-height: 1; margin-bottom: 8px; }
  .ts-stat-num.emerald { color: #059669; }
  .ts-stat-num.navy { color: #0f172a; }
  .ts-stat-num.gold { color: #d97706; }
  .ts-stat-label { font-size: 0.82rem; color: #64748b; font-weight: 500; }

  /* ─── Section Base ───────────────────────────────────────────── */
  .ts-section { padding: 96px 24px; }
  .ts-container { max-width: 1200px; margin: 0 auto; }
  .ts-section-header { text-align: center; max-width: 700px; margin: 0 auto 64px; }
  .ts-section-header h2 { font-size: clamp(1.8rem, 3.5vw, 2.8rem); font-weight: 800; color: #0f172a; letter-spacing: -0.02em; margin-bottom: 16px; line-height: 1.2; }
  .ts-section-header p { font-size: 1.05rem; color: #64748b; line-height: 1.75; }
  .ts-section-label { display: inline-block; font-size: 12px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #059669; margin-bottom: 12px; }

  /* ─── Services Grid ──────────────────────────────────────────── */
  .ts-services { background: #f8fafc; }
  .ts-services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  @media (max-width: 900px) { .ts-services-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 600px) { .ts-services-grid { grid-template-columns: 1fr; } }
  .ts-service-card { background: #fff; border-radius: 20px; border: 1px solid #e2e8f0; padding: 32px; transition: all 0.3s; position: relative; overflow: hidden; cursor: pointer; }
  .ts-service-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; transform: scaleX(0); transform-origin: left; transition: transform 0.3s ease; }
  .ts-service-card.green::before { background: linear-gradient(90deg, #10b981, #059669); }
  .ts-service-card.blue::before { background: linear-gradient(90deg, #0ea5e9, #3b82f6); }
  .ts-service-card.amber::before { background: linear-gradient(90deg, #f59e0b, #ef4444); }
  .ts-service-card.purple::before { background: linear-gradient(90deg, #8b5cf6, #6366f1); }
  .ts-service-card.teal::before { background: linear-gradient(90deg, #14b8a6, #10b981); }
  .ts-service-card.navy::before { background: linear-gradient(90deg, #0f172a, #1e40af); }
  .ts-service-card:hover { box-shadow: 0 16px 48px rgba(0,0,0,0.1); transform: translateY(-3px); border-color: #d1fae5; }
  .ts-service-card:hover::before { transform: scaleX(1); }
  .ts-svc-icon { width: 56px; height: 56px; border-radius: 16px; display: flex; align-items: center; justify-content: center; font-size: 26px; margin-bottom: 22px; }
  .ts-svc-icon.green { background: #f0fdf4; }
  .ts-svc-icon.blue { background: #f0f9ff; }
  .ts-svc-icon.amber { background: #fffbeb; }
  .ts-svc-icon.purple { background: #f5f3ff; }
  .ts-svc-icon.teal { background: #f0fdfa; }
  .ts-svc-icon.navy { background: #f1f5f9; }
  .ts-service-card h3 { font-size: 1.1rem; font-weight: 700; color: #1e293b; margin-bottom: 10px; }
  .ts-service-card p { font-size: 0.92rem; color: #64748b; line-height: 1.7; margin-bottom: 20px; }
  .ts-service-tag-list { display: flex; flex-wrap: wrap; gap: 6px; }
  .ts-service-tag { padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; background: #f1f5f9; color: #475569; transition: all 0.2s; }
  .ts-service-card:hover .ts-service-tag { background: #d1fae5; color: #065f46; }

  /* ─── How It Works ───────────────────────────────────────────── */
  .ts-process { background: #fff; }
  .ts-process-inner { background: #0f172a; border-radius: 32px; padding: 80px 64px; position: relative; overflow: hidden; }
  @media (max-width: 900px) { .ts-process-inner { padding: 48px 32px; } }
  .ts-process-glow1 { position: absolute; top: 0; right: 0; width: 400px; height: 400px; background: radial-gradient(circle, rgba(16,185,129,0.2), transparent 70%); pointer-events: none; }
  .ts-process-glow2 { position: absolute; bottom: 0; left: 0; width: 350px; height: 350px; background: radial-gradient(circle, rgba(245,158,11,0.15), transparent 70%); pointer-events: none; }
  .ts-process-header { text-align: center; margin-bottom: 64px; position: relative; z-index: 1; }
  .ts-process-header .ts-section-label { color: #6ee7b7; }
  .ts-process-header h2 { font-size: clamp(1.8rem, 3.5vw, 2.8rem); font-weight: 800; color: #fff; letter-spacing: -0.02em; }
  .ts-process-header p { font-size: 1.05rem; color: #94a3b8; margin-top: 16px; line-height: 1.7; }
  .ts-steps-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; position: relative; z-index: 1; }
  @media (max-width: 768px) { .ts-steps-grid { grid-template-columns: 1fr; } }
  .ts-step { position: relative; }
  .ts-step-num { width: 48px; height: 48px; border-radius: 14px; background: rgba(16,185,129,0.15); border: 1px solid rgba(16,185,129,0.3); display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 800; color: #34d399; margin-bottom: 24px; }
  .ts-step h3 { font-size: 1.15rem; font-weight: 700; color: #f1f5f9; margin-bottom: 12px; }
  .ts-step p { font-size: 0.92rem; color: #94a3b8; line-height: 1.7; }
  .ts-step-divider { position: absolute; top: 24px; left: 72px; right: -16px; height: 1px; background: linear-gradient(90deg, rgba(52,211,153,0.4), transparent); }
  @media (max-width: 768px) { .ts-step-divider { display: none; } }
  .ts-step:last-child .ts-step-divider { display: none; }

  /* ─── Benefits ───────────────────────────────────────────────── */
  .ts-benefits { background: #f8fafc; }
  .ts-benefits-inner { display: grid; grid-template-columns: 1.1fr 1fr; gap: 80px; align-items: center; }
  @media (max-width: 900px) { .ts-benefits-inner { grid-template-columns: 1fr; gap: 48px; } }
  .ts-benefits-left h2 { font-size: clamp(1.8rem, 3.5vw, 2.8rem); font-weight: 800; color: #0f172a; letter-spacing: -0.02em; margin-bottom: 16px; line-height: 1.2; }
  .ts-benefits-left > p { font-size: 1.05rem; color: #64748b; line-height: 1.75; margin-bottom: 40px; }
  .ts-benefit-list { display: flex; flex-direction: column; gap: 28px; }
  .ts-benefit { display: flex; gap: 16px; align-items: flex-start; }
  .ts-benefit-icon { flex-shrink: 0; width: 48px; height: 48px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 20px; }
  .ts-benefit-icon.green { background: #d1fae5; }
  .ts-benefit-icon.amber { background: #fef3c7; }
  .ts-benefit-icon.blue { background: #e0f2fe; }
  .ts-benefit-icon.purple { background: #ede9fe; }
  .ts-benefit h4 { font-size: 1rem; font-weight: 700; color: #1e293b; margin-bottom: 6px; }
  .ts-benefit p { font-size: 0.9rem; color: #64748b; line-height: 1.65; }
  .ts-benefits-visual { background: #fff; border-radius: 24px; border: 1px solid #e2e8f0; padding: 36px; box-shadow: 0 20px 60px rgba(0,0,0,0.06); }
  .ts-bv-header { display: flex; align-items: center; gap: 12px; margin-bottom: 28px; padding-bottom: 20px; border-bottom: 1px solid #f1f5f9; }
  .ts-bv-icon { width: 44px; height: 44px; background: linear-gradient(135deg, #059669, #10b981); border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
  .ts-bv-title { font-size: 15px; font-weight: 700; color: #0f172a; }
  .ts-bv-sub { font-size: 12px; color: #94a3b8; margin-top: 2px; }
  .ts-checklist { display: flex; flex-direction: column; gap: 14px; margin-bottom: 28px; }
  .ts-check-item { display: flex; align-items: center; gap: 12px; font-size: 14px; color: #374151; font-weight: 500; }
  .ts-check-mark { width: 22px; height: 22px; border-radius: 50%; background: #d1fae5; display: flex; align-items: center; justify-content: center; font-size: 12px; flex-shrink: 0; color: #059669; font-weight: 700; }
  .ts-bv-divider { height: 1px; background: #f1f5f9; margin-bottom: 24px; }
  .ts-bv-saving-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
  .ts-bv-saving-label { font-size: 13px; color: #64748b; }
  .ts-bv-saving-val { font-size: 14px; font-weight: 700; color: #059669; }
  .ts-bv-note { font-size: 12px; color: #94a3b8; line-height: 1.5; margin-top: 16px; padding: 12px; background: #f8fafc; border-radius: 10px; }

  /* ─── Tax Types ──────────────────────────────────────────────── */
  .ts-types { background: #fff; }
  .ts-types-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  @media (max-width: 900px) { .ts-types-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .ts-types-grid { grid-template-columns: 1fr; } }
  .ts-type-card { background: #f8fafc; border: 1px solid #f1f5f9; border-radius: 20px; padding: 28px; transition: all 0.3s; cursor: pointer; }
  .ts-type-card:hover { background: #fff; border-color: #6ee7b7; box-shadow: 0 12px 40px rgba(0,0,0,0.08); transform: translateY(-2px); }
  .ts-type-emoji { font-size: 28px; margin-bottom: 16px; display: block; }
  .ts-type-card h3 { font-size: 1.05rem; font-weight: 700; color: #1e293b; margin-bottom: 8px; }
  .ts-type-card p { font-size: 0.88rem; color: #64748b; line-height: 1.65; margin-bottom: 16px; }
  .ts-type-chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .ts-type-chip { padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; background: #e2e8f0; color: #475569; }
  .ts-type-card:hover .ts-type-chip { background: #d1fae5; color: #065f46; }

  /* ─── Trust Section ─────────────────────────────────────────── */
  .ts-trust { background: #0f172a; color: #f8fafc; position: relative; overflow: hidden; }
  .ts-trust-glow1 { position: absolute; top: 0; right: 20%; width: 500px; height: 500px; background: radial-gradient(circle, rgba(16,185,129,0.15), transparent 70%); pointer-events: none; }
  .ts-trust-glow2 { position: absolute; bottom: 0; left: -5%; width: 400px; height: 400px; background: radial-gradient(circle, rgba(245,158,11,0.1), transparent 70%); pointer-events: none; }
  .ts-trust .ts-section-header h2 { color: #f1f5f9; }
  .ts-trust .ts-section-header p { color: #94a3b8; }
  .ts-trust .ts-section-label { color: #6ee7b7; }
  .ts-trust-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; position: relative; z-index: 1; }
  @media (max-width: 900px) { .ts-trust-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .ts-trust-grid { grid-template-columns: 1fr; } }
  .ts-trust-card { background: rgba(30,41,59,0.5); border: 1px solid rgba(71,85,105,0.4); border-radius: 20px; padding: 32px; transition: border-color 0.3s; }
  .ts-trust-card:hover { border-color: rgba(16,185,129,0.4); }
  .ts-trust-card-icon { font-size: 28px; margin-bottom: 20px; display: block; transition: transform 0.2s; }
  .ts-trust-card:hover .ts-trust-card-icon { transform: scale(1.15); }
  .ts-trust-card h4 { font-size: 1rem; font-weight: 700; color: #f1f5f9; margin-bottom: 10px; }
  .ts-trust-card p { font-size: 0.88rem; color: #94a3b8; line-height: 1.7; }

  /* ─── FAQ ───────────────────────────────────────────────────── */
  .ts-faq { background: #fff; }
  .ts-faq-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
  @media (max-width: 768px) { .ts-faq-grid { grid-template-columns: 1fr; } }
  .ts-faq-item { background: #f8fafc; border: 1px solid #f1f5f9; border-radius: 16px; padding: 28px; transition: all 0.2s; }
  .ts-faq-item:hover { background: #fff; border-color: #d1fae5; box-shadow: 0 4px 16px rgba(0,0,0,0.05); }
  .ts-faq-q { font-size: 1rem; font-weight: 700; color: #0f172a; margin-bottom: 10px; display: flex; align-items: flex-start; gap: 10px; }
  .ts-faq-q-icon { width: 22px; height: 22px; border-radius: 6px; background: #d1fae5; display: flex; align-items: center; justify-content: center; font-size: 12px; color: #059669; flex-shrink: 0; margin-top: 1px; }
  .ts-faq-a { font-size: 0.9rem; color: #64748b; line-height: 1.7; }

  /* ─── CTA ────────────────────────────────────────────────────── */
  .ts-cta { background: #fff; border-top: 1px solid #f1f5f9; padding: 96px 24px; }
  .ts-cta-inner { background: #0f172a; border-radius: 32px; padding: 80px 48px; text-align: center; position: relative; overflow: hidden; max-width: 1000px; margin: 0 auto; }
  @media (max-width: 600px) { .ts-cta-inner { padding: 48px 24px; } }
  .ts-cta-glow1 { position: absolute; top: 0; right: 0; width: 360px; height: 360px; background: radial-gradient(circle, rgba(16,185,129,0.25), transparent 70%); pointer-events: none; }
  .ts-cta-glow2 { position: absolute; bottom: 0; left: 0; width: 300px; height: 300px; background: radial-gradient(circle, rgba(245,158,11,0.18), transparent 70%); pointer-events: none; }
  .ts-cta-inner h2 { font-size: clamp(2rem, 4vw, 3rem); font-weight: 900; color: #fff; letter-spacing: -0.03em; line-height: 1.15; margin-bottom: 20px; position: relative; z-index: 1; }
  .ts-cta-inner p { font-size: 1.1rem; color: #94a3b8; font-weight: 300; max-width: 560px; margin: 0 auto 40px; line-height: 1.75; position: relative; z-index: 1; }
  .ts-cta-btns { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; position: relative; z-index: 1; margin-bottom: 40px; }
  .ts-cta-primary { padding: 16px 32px; background: linear-gradient(135deg, #059669, #10b981); color: #fff; border: none; border-radius: 12px; font-size: 16px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s; box-shadow: 0 4px 20px rgba(5,150,105,0.4); }
  .ts-cta-primary:hover { background: linear-gradient(135deg, #047857, #059669); transform: translateY(-1px); box-shadow: 0 8px 28px rgba(5,150,105,0.5); }
  .ts-cta-secondary { padding: 16px 32px; background: rgba(255,255,255,0.06); color: #fff; border: 1px solid rgba(255,255,255,0.15); border-radius: 12px; font-size: 16px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
  .ts-cta-secondary:hover { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.25); }
  .ts-cta-badges { display: flex; gap: 24px; justify-content: center; flex-wrap: wrap; position: relative; z-index: 1; }
  .ts-cta-badge { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #94a3b8; font-weight: 500; }
  .ts-cta-badge-check { color: #34d399; font-size: 14px; }
`;

const fadeIn = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.55 },
};

const stagger = (i: number) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.5, delay: i * 0.1 },
});

// ─── Data ────────────────────────────────────────────────────────

const services = [
  {
    icon: '📋',
    color: 'green',
    slug: 'tax-preparation',
    title: 'Tax Preparation',
    description: 'Accurate, complete tax returns for individuals and small businesses. We identify every deduction and credit to maximize your refund.',
    tags: ['Federal & State', 'W-2 / 1099', 'Self-Employed'],
  },
  {
    icon: '🗓️',
    color: 'blue',
    slug: 'tax-planning',
    title: 'Year-Round Tax Planning',
    description: 'Proactive strategy throughout the year — not just at tax season — to minimize your liability and avoid surprises.',
    tags: ['Quarterly Reviews', 'Projections', 'Entity Strategy'],
  },
  {
    icon: '🛡️',
    color: 'amber',
    slug: 'irs-representation',
    title: 'IRS Representation',
    description: 'Professional representation for audits, notices, appeals, and collections. We handle the IRS so you don\'t have to.',
    tags: ['Audit Defense', 'Penalty Relief', 'Installment Plans'],
  },
  {
    icon: '📊',
    color: 'purple',
    slug: 'business-consulting',
    title: 'Business Tax Consulting',
    description: 'Strategic tax advice for entity selection, growth planning, acquisitions, and exit strategies — tailored to your business.',
    tags: ['Entity Selection', 'M&A Tax', 'Growth Strategy'],
  },
  {
    icon: '🔢',
    color: 'teal',
    slug: 'quarterly-estimates',
    title: 'Quarterly Estimates',
    description: 'Precise estimated tax calculations to keep you compliant, avoid underpayment penalties, and manage cash flow confidently.',
    tags: ['Safe Harbor', 'Cash Flow', 'Penalty Prevention'],
  },
  {
    icon: '📑',
    color: 'navy',
    slug: 'payroll-sales-tax',
    title: 'Payroll & Sales Tax',
    description: 'Full payroll tax compliance including 941s, state withholding, and W-2/W-3 filings. Multi-state sales tax nexus management.',
    tags: ['Payroll Tax', 'Sales Tax Nexus', '941 Filing'],
  },
];

const steps = [
  {
    num: '01',
    title: 'Free Consultation',
    description: 'We start with a complimentary discovery call to understand your tax situation, business structure, and financial goals.',
  },
  {
    num: '02',
    title: 'Custom Tax Strategy',
    description: 'Our CPAs design a personalized tax plan, identifying every legal deduction, credit, and opportunity specific to you.',
  },
  {
    num: '03',
    title: 'File & Save',
    description: 'We prepare and file everything accurately, on time. Then we track outcomes and refine your strategy for next year.',
  },
];

const benefits = [
  {
    icon: '💰',
    color: 'green',
    title: 'Maximize Every Dollar',
    description: 'Our CPAs routinely uncover deductions and credits that clients miss on their own — often saving multiples of our fee.',
  },
  {
    icon: '⏱️',
    color: 'amber',
    title: 'Reclaim Your Time',
    description: 'Stop drowning in receipts and tax forms. We handle the complexity so you can focus on running and growing your business.',
  },
  {
    icon: '🔒',
    color: 'blue',
    title: 'Penalty-Free Compliance',
    description: 'Accurate, on-time filings across federal, state, and local jurisdictions. No missed deadlines, no costly penalties.',
  },
  {
    icon: '📈',
    color: 'purple',
    title: 'Strategic Advantage',
    description: 'Go beyond compliance. Our proactive tax planning turns your tax strategy into a competitive advantage for growth.',
  },
];

const taxTypes = [
  {
    emoji: '🏢',
    title: 'S-Corporations & C-Corps',
    description: 'Entity-level tax optimization, reasonable compensation analysis, and shareholder tax planning.',
    chips: ['Form 1120', 'Form 1120-S', 'K-1 Analysis'],
    slug: 's-corps-c-corps',
  },
  {
    emoji: '🤝',
    title: 'Partnerships & LLCs',
    description: 'Multi-member LLC and partnership returns with allocation strategy and self-employment tax minimization.',
    chips: ['Form 1065', 'Schedule K-1', 'SE Tax Planning'],
    slug: 'partnerships-llcs',
  },
  {
    emoji: '👤',
    title: 'Sole Proprietors & Freelancers',
    description: 'Comprehensive Schedule C preparation, home office deductions, vehicle tracking, and QBI optimization.',
    chips: ['Schedule C', 'QBI Deduction', 'Home Office'],
    slug: 'sole-proprietors',
  },
  {
    emoji: '🏠',
    title: 'Real Estate Investors',
    description: 'Cost segregation, depreciation strategies, 1031 exchanges, and passive activity rules maximization.',
    chips: ['Schedule E', '1031 Exchange', 'Depreciation'],
    slug: 'real-estate',
  },
  {
    emoji: '🌐',
    title: 'International & Multi-State',
    description: 'Multi-state nexus analysis, apportionment, FBAR filings, and foreign income exclusion strategies.',
    chips: ['FBAR', 'SALT Planning', 'Nexus Analysis'],
    slug: 'international',
  },
  {
    emoji: '🌱',
    title: 'Startups & Growth Stage',
    description: 'R&D tax credits, qualified small business stock, NOL carryforwards, and investor-friendly tax structures.',
    chips: ['R&D Credit', 'QSBS', 'NOL Strategy'],
    slug: 'startups',
  },
];

const trustCards = [
  { icon: '🏅', title: 'Certified Public Accountants', description: 'All tax work performed by licensed CPAs with deep small business specialization and ongoing continuing education.' },
  { icon: '✅', title: '100% Accuracy Guarantee', description: 'We stand behind every return. If an error on our part results in a penalty, we pay it — no questions asked.' },
  { icon: '🔐', title: 'Bank-Grade Data Security', description: 'Your financial data is protected with AES-256 encryption, secure client portal, and strict data handling protocols.' },
  { icon: '📞', title: 'Year-Round Support', description: 'Tax questions don\'t wait for April. Your dedicated tax advisor is available year-round via phone, email, and portal.' },
  { icon: '⚡', title: 'Fast Turnaround', description: 'Most individual returns completed within 5 business days. Business returns within 10 days — guaranteed.' },
  { icon: '💸', title: 'Transparent, Flat Pricing', description: 'No surprises. Flat-fee pricing means you know the exact cost upfront — no hourly billing, no hidden charges.' },
];

const faqs = [
  { q: 'When should I start tax planning?', a: 'Tax planning is most effective year-round — not just in April. Decisions made in January through December have a far bigger impact on your tax bill than anything done at filing time.' },
  { q: 'What records do I need to provide?', a: 'We guide you through exactly what\'s needed via our secure client portal checklist. Most clients are surprised at how straightforward the process is.' },
  { q: 'Can you handle multiple states?', a: 'Absolutely. We specialize in multi-state tax compliance, including nexus analysis, state apportionment, and optimizing your overall state tax burden.' },
  { q: 'What if I get audited?', a: 'Every return we prepare includes audit support. If you receive an IRS or state notice, contact us immediately — representation is included at no extra charge.' },
  { q: 'Do you work with my bookkeeper or accountant?', a: 'Yes. We collaborate seamlessly with your existing team. We can also connect you with bookkeeping partners if needed for a fully integrated solution.' },
  { q: 'How much can I expect to save?', a: 'Results vary by situation, but clients who switch to a proactive tax strategy typically save 10–25% more on their annual tax bill compared to DIY or reactive filing.' },
];

// ─── Page ─────────────────────────────────────────────────────────

const TaxServicesOverview: React.FC<{ onSelectService: (slug: string) => void; onSelectClient?: (slug: string) => void }> = ({ onSelectService, onSelectClient }) => {
  return (
    <div className="ts-page">
      <style>{styles}</style>

      {/* ── Hero ────────────────────────────────────────────────── */}
      <section className="ts-hero">
        <div className="ts-hero-blob1" />
        <div className="ts-hero-blob2" />
        <div className="ts-hero-inner">
          <div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
              <div className="ts-hero-badge">
                <span className="ts-badge-dot" />
                Trusted by 2,800+ small business owners
              </div>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
              Tax Services That <span className="ts-gradient-text">Actually Save</span> You{' '}
              <span className="ts-gold-text">Money</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              Stop leaving money on the table. Our CPAs go beyond filing — we build year-round strategies that minimize your tax burden, protect your business, and fuel your growth.
            </motion.p>
            <motion.div className="ts-btn-group" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
              <button type="button" className="ts-btn-primary">
                Schedule Free Consultation →
              </button>
              <button type="button" className="ts-btn-secondary">
                See Our Services
              </button>
            </motion.div>
          </div>

          <motion.div className="ts-hero-visual" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
            <div className="ts-visual-header">
              <div className="ts-visual-title">
                <p>Tax Savings Summary</p>
                <p>2024 Business Return</p>
              </div>
              <div className="ts-visual-icon">💰</div>
            </div>
            {[
              { dot: '#10b981', label: 'Home Office Deduction', amount: '+$3,840' },
              { dot: '#f59e0b', label: 'Vehicle & Mileage', amount: '+$2,210' },
              { dot: '#0ea5e9', label: 'Health Insurance Premium', amount: '+$4,600' },
              { dot: '#8b5cf6', label: 'QBI Deduction (20%)', amount: '+$8,100' },
              { dot: '#ef4444', label: 'Retirement Contributions', amount: '+$5,500' },
            ].map((item, i) => (
              <div className="ts-saving-item" key={i}>
                <div className="ts-saving-left">
                  <span className="ts-saving-dot" style={{ background: item.dot }} />
                  {item.label}
                </div>
                <span className="ts-saving-right">{item.amount}</span>
              </div>
            ))}
            <div className="ts-visual-total">
              <span>Total Savings Found</span>
              <span>$24,250</span>
            </div>
            <div className="ts-visual-badge">
              <span className="ts-visual-badge-dot" />
              <span className="ts-badge-text">Accuracy verified — filed & accepted by IRS within 3 days</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Stats Bar ───────────────────────────────────────────── */}
      <div className="ts-stats">
        <div className="ts-stats-grid">
          {[
            { num: '2,800+', label: 'Returns Filed Annually', cls: 'emerald' },
            { num: '$1.2M+', label: 'Total Tax Savings Delivered', cls: 'gold' },
            { num: '100%', label: 'Accuracy Guarantee', cls: 'navy' },
            { num: '15+', label: 'Years of Expert Experience', cls: 'emerald' },
          ].map((s, i) => (
            <motion.div className="ts-stat" key={i} {...stagger(i)}>
              <div className={`ts-stat-num ${s.cls}`}>{s.num}</div>
              <div className="ts-stat-label">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── Services ────────────────────────────────────────────── */}
      <section className="ts-section ts-services">
        <div className="ts-container">
          <motion.div className="ts-section-header" {...fadeIn}>
            <span className="ts-section-label">What We Do</span>
            <h2>Comprehensive Tax Solutions for Every Stage</h2>
            <p>From first-year freelancers to established multi-entity businesses — we have the expertise and technology to handle your full tax picture.</p>
          </motion.div>
          <div className="ts-services-grid">
            {services.map((svc, i) => (
              <motion.div
                className={`ts-service-card ${svc.color}`}
                key={i}
                {...stagger(i)}
                onClick={() => onSelectService(svc.slug)}
                style={{ cursor: 'pointer' }}
              >
                <div className={`ts-svc-icon ${svc.color}`}>{svc.icon}</div>
                <h3>{svc.title}</h3>
                <p>{svc.description}</p>
                <div className="ts-service-tag-list">
                  {svc.tags.map((t, j) => <span className="ts-service-tag" key={j}>{t}</span>)}
                </div>
                <div style={{ marginTop: '16px', fontSize: '13px', fontWeight: 700, color: '#059669', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Learn more →
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────────── */}
      <section className="ts-section ts-process">
        <div className="ts-container">
          <div className="ts-process-inner">
            <div className="ts-process-glow1" />
            <div className="ts-process-glow2" />
            <motion.div className="ts-process-header" {...fadeIn}>
              <span className="ts-section-label">Our Process</span>
              <h2>Simple. Strategic. Stress-Free.</h2>
              <p>Three steps to a smarter tax strategy — and a lot more money staying in your pocket.</p>
            </motion.div>
            <div className="ts-steps-grid">
              {steps.map((step, i) => (
                <motion.div className="ts-step" key={i} {...stagger(i)}>
                  {i < steps.length - 1 && <div className="ts-step-divider" />}
                  <div className="ts-step-num">{step.num}</div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Benefits ─────────────────────────────────────────────── */}
      <section className="ts-section ts-benefits">
        <div className="ts-container">
          <div className="ts-benefits-inner">
            <motion.div className="ts-benefits-left" {...fadeIn}>
              <span className="ts-section-label">Why BizServe Tax</span>
              <h2>Tax Expertise That Works as Hard as You Do</h2>
              <p>We're not just tax preparers — we're strategic partners who understand the financial realities of running a small business.</p>
              <div className="ts-benefit-list">
                {benefits.map((b, i) => (
                  <motion.div className="ts-benefit" key={i} {...stagger(i)}>
                    <div className={`ts-benefit-icon ${b.color}`}>{b.icon}</div>
                    <div>
                      <h4>{b.title}</h4>
                      <p>{b.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div className="ts-benefits-visual" initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="ts-bv-header">
                <div className="ts-bv-icon">📊</div>
                <div>
                  <div className="ts-bv-title">Your Savings Snapshot</div>
                  <div className="ts-bv-sub">Average BizServe client — FY 2024</div>
                </div>
              </div>
              <div className="ts-checklist">
                {[
                  'All federal & state returns filed on time',
                  'Every deduction and credit identified',
                  'Quarterly estimates calculated and scheduled',
                  'IRS correspondence handled for you',
                  'Year-end planning session included',
                  'Secure document storage & portal access',
                ].map((item, i) => (
                  <div className="ts-check-item" key={i}>
                    <div className="ts-check-mark">✓</div>
                    {item}
                  </div>
                ))}
              </div>
              <div className="ts-bv-divider" />
              {[
                { label: 'Average tax savings vs. DIY', val: '$8,400 / yr' },
                { label: 'Average time saved per year', val: '42 hours' },
                { label: 'Penalty avoidance rate', val: '99.7%' },
              ].map((row, i) => (
                <div className="ts-bv-saving-row" key={i}>
                  <span className="ts-bv-saving-label">{row.label}</span>
                  <span className="ts-bv-saving-val">{row.val}</span>
                </div>
              ))}
              <div className="ts-bv-note">
                Based on aggregate client data. Individual results vary by business type, size, and prior tax management.
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Tax Types ─────────────────────────────────────────────── */}
      <section className="ts-section ts-types">
        <div className="ts-container">
          <motion.div className="ts-section-header" {...fadeIn}>
            <span className="ts-section-label">Who We Serve</span>
            <h2>Specialized Tax Expertise for Every Business Type</h2>
            <p>Whether you're a solo consultant or a growing multi-entity company, we have deep expertise in your specific tax situation.</p>
          </motion.div>
          <div className="ts-types-grid">
            {taxTypes.map((type, i) => (
              <motion.div
                className="ts-type-card"
                key={i}
                {...stagger(i)}
                onClick={() => onSelectClient && onSelectClient(type.slug)}
                style={{ cursor: 'pointer' }}
              >
                <span className="ts-type-emoji">{type.emoji}</span>
                <h3>{type.title}</h3>
                <p>{type.description}</p>
                <div className="ts-type-chips">
                  {type.chips.map((c, j) => <span className="ts-type-chip" key={j}>{c}</span>)}
                </div>
                <div style={{ marginTop: '16px', fontSize: '13px', fontWeight: 700, color: '#059669', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Learn more →
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Trust ─────────────────────────────────────────────────── */}
      <section className="ts-section ts-trust">
        <div className="ts-trust-glow1" />
        <div className="ts-trust-glow2" />
        <div className="ts-container">
          <motion.div className="ts-section-header" {...fadeIn}>
            <span className="ts-section-label">Our Commitment</span>
            <h2>The Standard We Hold Ourselves To</h2>
            <p>Every client engagement is built on accuracy, transparency, and results. Here's what you can always count on.</p>
          </motion.div>
          <div className="ts-trust-grid">
            {trustCards.map((card, i) => (
              <motion.div className="ts-trust-card" key={i} {...stagger(i)}>
                <span className="ts-trust-card-icon">{card.icon}</span>
                <h4>{card.title}</h4>
                <p>{card.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────── */}
      <section className="ts-section ts-faq">
        <div className="ts-container">
          <motion.div className="ts-section-header" {...fadeIn}>
            <span className="ts-section-label">Common Questions</span>
            <h2>Answers to What Business Owners Ask Most</h2>
            <p>Transparency is part of our service. If your question isn't here, reach out — we respond within one business day.</p>
          </motion.div>
          <div className="ts-faq-grid">
            {faqs.map((faq, i) => (
              <motion.div className="ts-faq-item" key={i} {...stagger(i)}>
                <div className="ts-faq-q">
                  <div className="ts-faq-q-icon">?</div>
                  {faq.q}
                </div>
                <div className="ts-faq-a">{faq.a}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────── */}
      <section className="ts-cta">
        <div className="ts-cta-inner">
          <div className="ts-cta-glow1" />
          <div className="ts-cta-glow2" />
          <motion.h2 {...fadeIn}>
            Ready to Keep More of What You Earn?
          </motion.h2>
          <motion.p {...{ ...fadeIn, transition: { duration: 0.55, delay: 0.1 } }}>
            Join thousands of small business owners who've stopped overpaying and started building real financial confidence — one smart tax decision at a time.
          </motion.p>
          <motion.div className="ts-cta-btns" {...{ ...fadeIn, transition: { duration: 0.5, delay: 0.2 } }}>
            <button type="button" className="ts-cta-primary">
              Schedule Free Consultation →
            </button>
            <button type="button" className="ts-cta-secondary">
              Get an Instant Quote
            </button>
          </motion.div>
          <motion.div className="ts-cta-badges" {...{ ...fadeIn, transition: { duration: 0.5, delay: 0.3 } }}>
            {['Free initial consultation', 'Licensed CPAs only', 'Maximum refund guarantee', 'No commitment required'].map((b, i) => (
              <div className="ts-cta-badge" key={i}>
                <span className="ts-cta-badge-check">✓</span>
                {b}
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

const TaxServices: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const segments = location.pathname.split('/').filter(Boolean);
  const baseSegments = segments.slice(0, 2);
  const basePath = `/${baseSegments.join('/')}`;

  const serviceSlug = (searchParams.get('service') || '').toLowerCase();
  const clientSlug = (searchParams.get('client') || '').toLowerCase();

  const handleSelectService = (slug: string) => {
    if (!slug) return;
    const next = new URLSearchParams(searchParams);
    next.set('service', slug);
    navigate(`${basePath}?${next.toString()}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectClient = (slug: string) => {
    if (!slug) return;
    const next = new URLSearchParams(searchParams);
    next.set('client', slug);
    navigate(`${basePath}?${next.toString()}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('service');
    next.delete('client');
    navigate(`${basePath}?${next.toString()}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderContent = () => {
    if (serviceSlug) {
      switch (serviceSlug) {
        case 'tax-preparation':    return <TaxPreparation onBack={handleBack} />;
        case 'tax-planning':       return <TaxPlanning onBack={handleBack} />;
        case 'irs-representation': return <IRSRepresentation onBack={handleBack} />;
        case 'business-consulting':return <BusinessTaxConsulting onBack={handleBack} />;
        case 'quarterly-estimates':return <QuarterlyEstimates onBack={handleBack} />;
        case 'payroll-sales-tax':  return <PayrollSalesTax onBack={handleBack} />;
      }
    }
    if (clientSlug) {
      switch (clientSlug) {
        case 's-corps-c-corps':  return <TaxForSCorps onBack={handleBack} />;
        case 'partnerships-llcs':return <TaxForPartnerships onBack={handleBack} />;
        case 'sole-proprietors': return <TaxForFreelancers onBack={handleBack} />;
        case 'real-estate':      return <TaxForRealEstate onBack={handleBack} />;
        case 'international':    return <TaxForInternational onBack={handleBack} />;
        case 'startups':         return <TaxForStartups onBack={handleBack} />;
      }
    }
    return <TaxServicesOverview onSelectService={handleSelectService} onSelectClient={handleSelectClient} />;
  };

  return <>{renderContent()}</>;
};

export default TaxServices;
